import { useCallback, useEffect, useRef, useState } from 'react';
import { PLANS, type PlanId } from '../utils/billing/plans';
import { apiService } from '../services/api';

interface FamilyPlanData {
  planId: PlanId;
  status: string | null;
  periodEnd: string | null;
  priceId: string | null;
}

const defaultData: FamilyPlanData = { planId: 'basic', status: null, periodEnd: null, priceId: null };

export function useEntitlements(familyId?: string) {
  const [data, setData] = useState<FamilyPlanData>(() => {
    if (!familyId) return defaultData;
    try {
      const cached = sessionStorage.getItem(`family-plan-${familyId}`) as PlanId | null;
      if (cached === 'basic' || cached === 'pro' || cached === 'max') {
        return { ...defaultData, planId: cached };
      }
    } catch {}
    return defaultData;
  });
  const inFlightRef = useRef<Promise<void> | null>(null);

  const refreshPlan = useCallback(async () => {
    if (!familyId) return;
    if (inFlightRef.current) {
      try { await inFlightRef.current; } catch {};
      return;
    }
    const p = (async () => {
      try {
        const res = await apiService.getFamilyPlan(familyId);
        const newPlan = (res.planId as PlanId) || 'basic';
        setData({
          planId: newPlan,
          status: res.status || null,
          periodEnd: res.periodEnd || null,
          priceId: res.priceId || null,
        });
        try { sessionStorage.setItem(`family-plan-${familyId}`, newPlan); } catch {}
      } catch {
        // Nie zmieniaj planu przy błędzie chwilowym – zachowaj ostatnio znany
      }
    })();
    inFlightRef.current = p;
    try { await p; } finally { inFlightRef.current = null; }
  }, [familyId]);

  // Initial load
  useEffect(() => {
    if (!familyId) return;
    // Szybki odczyt z sessionStorage (już w useState initializer)
    void refreshPlan();
  }, [familyId, refreshPlan]);

  // Odśwież na focus/visibilitychange (PWA, iOS, powroty do aplikacji)
  useEffect(() => {
    if (!familyId) return;
    const onFocus = () => { void refreshPlan(); };
    const onVisibility = () => { if (document.visibilityState === 'visible') void refreshPlan(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [familyId, refreshPlan]);

  // Polling co 30s
  useEffect(() => {
    if (!familyId) return;
    const id = setInterval(() => { void refreshPlan(); }, 30000);
    return () => clearInterval(id);
  }, [familyId, refreshPlan]);

  return {
    plan: PLANS[data.planId],
    planId: data.planId,
    status: data.status,
    periodEnd: data.periodEnd,
    priceId: data.priceId,
    refreshPlan,
  };
}
